import { useState, useEffect, useCallback } from 'react';
import { Card, Text, Group, Badge, Button, Stack, Loader, ActionIcon, Tooltip, Alert } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { Calendar, Link as LinkIcon, RefreshCw, X, AlertTriangle } from 'react-feather';
import { billcomApi } from '../../../services/api/billcom';
import type { VendorMapping } from '../../../services/api/billcom';
import { LinkVendorModal } from '../../admin/compensation-settings/components/LinkVendorModal';
import { formatDate } from '../../../utils/formatters';
import { CalendlyBookingHealthAlerts } from '../../../components/coaches/CalendlyBookingHealthAlerts';
import { CalendlyDurationList } from '../../../components/coaches/CalendlyDurationList';
import { MissingMeetingLinkAlerts } from '../../../components/coaches/MissingMeetingLinkAlerts';
import type { CalendlyBookingHealth, MissingMeetingLink } from '../../../types';

interface ConnectionsCardProps {
  coachUserId: string;
  coachName: string;
  coachEmail: string;
  calendlyConnectedAt?: string | null;
  /** MED-1363: what can stop participant bookings. Null when unknown. */
  calendlyBookingHealth?: CalendlyBookingHealth | null;
  /** MED-1365 QA round 6: cohort platforms with no link on the coach's profile. */
  missingMeetingLinks?: MissingMeetingLink[] | null;
}

export function ConnectionsCard({
  coachUserId,
  coachName,
  coachEmail,
  calendlyConnectedAt = null,
  calendlyBookingHealth = null,
  missingMeetingLinks = null,
}: ConnectionsCardProps) {
  const calendlyConnected = !!calendlyConnectedAt;
  const [vendorMapping, setVendorMapping] = useState<VendorMapping | null>(null);
  const [loading, setLoading] = useState(true);
  const [linkModalOpened, { open: openLinkModal, close: closeLinkModal }] = useDisclosure(false);

  const fetchVendorMapping = useCallback(async () => {
    try {
      setLoading(true);
      const data = await billcomApi.getVendorMapping(coachUserId);
      setVendorMapping(data);
    } catch {
      setVendorMapping(null);
    } finally {
      setLoading(false);
    }
  }, [coachUserId]);

  useEffect(() => {
    fetchVendorMapping();
  }, [fetchVendorMapping]);

  const isBillComMapped = !!vendorMapping?.billcom_vendor_id;

  // MED-1178: unlink the coach's Bill.com vendor. This surface isn't
  // cohort-scoped, so (unlike the cohort Compensation tab) we can't tell which
  // cohort's payments reference the vendor — show a generic caution instead.
  const handleUnlinkVendor = useCallback(() => {
    modals.openConfirmModal({
      title: 'Unlink Bill.com vendor',
      children: (
        <Stack gap="sm">
          <Text size="sm">
            Unlink <strong>{coachName}</strong> from the Bill.com vendor{' '}
            <strong>{vendorMapping?.billcom_vendor_name || 'this vendor'}</strong>? You can link a
            different vendor afterward.
          </Text>
          <Alert color="warningAmber" variant="light" icon={<AlertTriangle size={16} />}>
            If this coach already has scheduled payments in any cohort, unlinking won&rsquo;t change
            them &mdash; review them in that cohort&rsquo;s Compensation tab and void or reschedule as
            needed.
          </Alert>
        </Stack>
      ),
      labels: { confirm: 'Unlink vendor', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await billcomApi.unlinkVendor(coachUserId);
          notifications.show({
            title: 'Vendor unlinked',
            message: `${coachName} is no longer linked to a Bill.com vendor.`,
            color: 'green',
          });
          fetchVendorMapping();
        } catch {
          notifications.show({
            title: 'Error',
            message: 'Failed to unlink vendor',
            color: 'red',
          });
        }
      },
    });
  }, [coachUserId, coachName, vendorMapping, fetchVendorMapping]);

  return (
    <>
      <Card
        p="lg"
        style={{ border: '1px solid #E8E6E1', borderRadius: 8, background: 'white' }}
      >
        <Text fw={700} size="md" mb="md">
          Connections
        </Text>

        <Stack gap="sm">
          {/* Calendly Status (read-only — only coaches can connect) */}
          <Group justify="space-between" align="center">
            <Group gap="xs">
              <Calendar size={14} />
              <Text size="sm">Calendly</Text>
            </Group>
            <Group gap="xs">
              {calendlyConnected && calendlyConnectedAt && (
                <Text size="xs" c="dimmed">
                  Connected {formatDate(calendlyConnectedAt)}
                </Text>
              )}
              <Badge
                size="sm"
                variant="light"
                color={calendlyConnected ? 'medleyGreen' : 'gray'}
              >
                {calendlyConnected ? 'Connected' : 'Disconnected'}
              </Badge>
            </Group>
          </Group>

          {calendlyConnected && calendlyBookingHealth?.durations && (
            <CalendlyDurationList
              durations={calendlyBookingHealth.durations}
              planStage={calendlyBookingHealth.plan_stage}
              audience="admin"
              offTwentyNames={calendlyBookingHealth.off_20min_event_types}
            />
          )}

          {calendlyConnected && (
            <CalendlyBookingHealthAlerts
              health={calendlyBookingHealth}
              audience="admin"
            />
          )}

          {/* MED-1365 QA round 6: also when Calendly is not connected */}
          <MissingMeetingLinkAlerts links={missingMeetingLinks} audience="admin" />

          {/* Bill.com Vendor Status */}
          <Group justify="space-between" align="center">
            <Group gap="xs">
              <LinkIcon size={14} />
              <Text size="sm">Bill.com</Text>
            </Group>
            {loading ? (
              <Loader size="xs" />
            ) : isBillComMapped ? (
              <Group gap={4} wrap="nowrap">
                <Tooltip
                  label={`Linked to ${vendorMapping?.billcom_vendor_name} (${vendorMapping?.billcom_vendor_id})`}
                >
                  <Badge size="sm" variant="light" color="medleyGreen">
                    Mapped
                  </Badge>
                </Tooltip>
                <Tooltip label="Change vendor">
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label="Change vendor"
                    onClick={openLinkModal}
                  >
                    <RefreshCw size={13} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Unlink vendor">
                  <ActionIcon
                    variant="subtle"
                    color="dangerRed"
                    size="sm"
                    aria-label="Unlink vendor"
                    onClick={handleUnlinkVendor}
                  >
                    <X size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            ) : (
              <Button
                size="compact-xs"
                variant="light"
                color="warningAmber"
                leftSection={<LinkIcon size={12} />}
                onClick={openLinkModal}
              >
                Link Vendor
              </Button>
            )}
          </Group>
        </Stack>
      </Card>

      <LinkVendorModal
        opened={linkModalOpened}
        onClose={closeLinkModal}
        coachUserId={coachUserId}
        coachName={coachName}
        coachEmail={coachEmail}
        onLinked={fetchVendorMapping}
      />
    </>
  );
}
